// apps/web/src/api/russia-carriers.ts — typed wrappers around
// `/api/russia-carriers/*` (#1166 backend + admin surface).
//
// Differs from `road-options.ts`:
//   - `DELETE` returns 204 (no body), the wrapper resolves to `void`;
//   - there is no `/:id/activate` endpoint — un-archive is a PATCH with
//     `active: true`;
//   - the list query uses `search` (not `q`) and has no cursor.

import {
  ListRussiaCarriersResponseSchema,
  RussiaCarrierSchema,
  type CreateRussiaCarrierBody,
  type ListRussiaCarriersQuery,
  type ListRussiaCarriersResponse,
  type RussiaCarrier,
  type UpdateRussiaCarrierBody,
} from '@magimp/shared';
import { request } from './client.js';

function withQuery(path: string, qs: string): string {
  return qs.length > 0 ? `${path}?${qs}` : path;
}

export function listRussiaCarriers(
  query: Partial<ListRussiaCarriersQuery> = {},
): Promise<ListRussiaCarriersResponse> {
  const params = new URLSearchParams();
  if (query.search) params.set('search', query.search);
  if (query.active !== undefined) params.set('active', String(query.active));
  return request<ListRussiaCarriersResponse>(
    withQuery('/russia-carriers', params.toString()),
    { method: 'GET' },
    {},
    ListRussiaCarriersResponseSchema,
  );
}

export function fetchRussiaCarrier(id: string): Promise<RussiaCarrier> {
  return request<RussiaCarrier>(
    `/russia-carriers/${encodeURIComponent(id)}`,
    { method: 'GET' },
    {},
    RussiaCarrierSchema,
  );
}

export function createRussiaCarrier(body: CreateRussiaCarrierBody): Promise<RussiaCarrier> {
  return request<RussiaCarrier>(
    '/russia-carriers',
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaCarrierSchema,
  );
}

export function patchRussiaCarrier(
  id: string,
  body: UpdateRussiaCarrierBody,
): Promise<RussiaCarrier> {
  return request<RussiaCarrier>(
    `/russia-carriers/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaCarrierSchema,
  );
}

// WHY: backend returns 204 + empty body on soft-archive (#1166). Use
//   the schema-less overload — `request` resolves to `undefined` on 204
//   without attempting JSON parsing.
export async function archiveRussiaCarrier(id: string): Promise<void> {
  await request<void>(`/russia-carriers/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
